import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  EditableCallbackParams,
  ValueFormatterParams,
  ValueGetterParams,
  ValueParserParams
} from 'ag-grid-community';
import { HEADER_TYPES, HeaderType } from 'src/constants/generic-constants';
import { ColumnWidths } from '../../ForecastGridConstants';
import { cellValueParser, forecastCurrencyFormatter, CellDataType } from 'src/utils/ag-grid-utils';
import { convertMonthFormatToDisplay, getQuarterFromMonth, getYearFromMonthYear } from 'src/utils/date-time-utilities';
import { isUserAuthorizedToEditThisRow } from '../helpers/AuthorizationHelper';
import { getQuartersFromMonths, sumUpColumns } from '../helpers/ColumnUtils';

const FORECAST_GRID_MONTH_BACKGROUND_COLORS = {
  QUARTERLY_YEARLY_TOTAL: 'var(--xpt-cell-quarterly-total-bg)',
  ACTUAL_COLUMN: 'var(--xpt-cell-actual-bg)'
} as const;

/**
 * Budget editable months window check (Planning R&O only; applies to Budget Leaders / Owners).
 * Returns true when the given forecast month column is editable under the configured window.
 * When no window is set (either bound null/blank), ALL months are editable (backward compatible).
 *
 * @param monthField - AG Grid month field in 'YYYYMM01' format (e.g. '20260401')
 * @param startMonth - inclusive window start in 'YYYY-MM' (or null/undefined for no window)
 * @param endMonth - inclusive window end in 'YYYY-MM' (or null/undefined for no window)
 */
export const isMonthWithinBudgetEditableWindow = (
  monthField: string,
  startMonth: string | null | undefined,
  endMonth: string | null | undefined
): boolean => {
  if (!startMonth || !endMonth) {
    return true; // No window configured → unrestricted (backward compatible)
  }
  if (!monthField || monthField.length < 6) {
    return true;
  }
  const monthYYYYMM = `${monthField.slice(0, 4)}-${monthField.slice(4, 6)}`;
  // Lexicographic compare on YYYY-MM is chronologically correct
  return monthYYYYMM >= startMonth && monthYYYYMM <= endMonth;
};

/**
 * Generates columns for each year, including quarterly and monthly groupings, for the forecast table.
 * @param {string[]} monthHeaders - Array of month headers in the format 'YYYYMM01'.
 * @param {'Actual' | 'Forecast'} headerFor - Indicates if the headers are for 'Actual' or 'Forecast'.
 * @param {boolean} isEditable - Boolean indicating if the columns are editable.
 * @param {string} userAlias - The alias of the current user.
 * @param {boolean} isFinanceAdminOrFinanceOwner - Boolean indicating if the user is a finance admin or owner.
 * @param {boolean} isBudgetLeader - Boolean indicating if the user is a budget leader.
 * @returns {ColDef[]} - Array of column definitions for the AG Grid.
 */
export const getActualForecastHeadersWithGrouping = (
  monthHeaders: string[],
  headerFor: HeaderType,
  isEditable: boolean,
  userAlias: string,
  isFinanceAdminOrFinanceOwner: boolean,
  isBudgetLeader: boolean,
  budgetEditableStartMonth?: string | null,
  budgetEditableEndMonth?: string | null
): ColDef[] => {
  const agGridHeaderData: ColDef[] = [];

  // Extract unique years from month headers
  const years = new Set<string>();
  monthHeaders?.forEach((monthHeader) => {
    years.add(getYearFromMonthYear(monthHeader));
  });

  years.forEach((year: string) => {
    // Filter month headers for the current year
    const monthsOfThisYear = monthHeaders?.filter((monthHeader) => monthHeader.startsWith(year.toString()));
    const quartersOfThisYear = getQuartersFromMonths(monthsOfThisYear);

    const quarterWithMonthlyHeaderInfo: ColDef[] = [];

    quartersOfThisYear?.forEach((quarter) => {
      // Filter month headers for the current quarter
      const monthsOfThisQuarter = monthsOfThisYear?.filter((month) => quarter === getQuarterFromMonth(month));

      // Generate month column definitions
      const monthColumnDefinitions: ColDef[] = generateMonthColumnDefinition(
        monthsOfThisQuarter,
        headerFor,
        isEditable,
        userAlias,
        isFinanceAdminOrFinanceOwner,
        isBudgetLeader,
        budgetEditableStartMonth,
        budgetEditableEndMonth
      );

      // Generate quarter column definitions including monthly columns
      generateMonthsWithQuarterColumnDefinition(quarterWithMonthlyHeaderInfo, quarter, monthColumnDefinitions, year, headerFor);
    });

    // Generate year column definitions including quarter columns
    const yearColumnDefinitions = generateYearColumnDefinitions(year, quarterWithMonthlyHeaderInfo, headerFor);
    agGridHeaderData.push(yearColumnDefinitions);
  });

  const finalOutput: ColDef[] = [];
  agGridHeaderData.forEach((year: any) => {
    year.children?.forEach((quarter: any) => {
      if (quarter.children?.length > 0) {
        quarter.children?.forEach((monthlyData: any) => {
          finalOutput.push({ ...monthlyData, columnGroupShow: 'open' });
        });
      } else {
        finalOutput.push({ ...quarter, columnGroupShow: 'open' });
        finalOutput.push({ ...quarter, columnGroupShow: 'closed' });
      }
    });
  });

  return agGridHeaderData;
};

/**
 * Generates column definitions for the months of a quarter
 * @param monthsOfThisQuarter - Array of month identifiers for the quarter. month in the format 'YYYYMM01'.
 * @param headerFor - The header type (e.g., 'Actual', 'Forecast')
 * @param isEditable - Boolean indicating if the column is editable
 * @param userAlias - The alias of the current user
 * @param isFinanceAdminOrFinanceOwner - Boolean indicating if the user is a finance admin or owner
 * @param isBudgetLeader - Boolean indicating if the user is a budget leader
 * @returns ColDef[] - Array of column definitions for the months
 */
export const generateMonthColumnDefinition = (
  monthsOfThisQuarter: string[],
  headerFor: string,
  isEditable: boolean,
  userAlias: string,
  isFinanceAdminOrFinanceOwner: boolean,
  isBudgetLeader: boolean,
  budgetEditableStartMonth?: string | null,
  budgetEditableEndMonth?: string | null
): ColDef<any, any>[] => {
  return monthsOfThisQuarter?.map((monthInQuarter) => {
    // Budget editable months window (Planning R&O only) restricts Budget Leaders / Owners to the
    // configured month range. Finance Admin / Owner are unrestricted. No window → all months editable.
    const monthEditableUnderBudgetWindow =
      isFinanceAdminOrFinanceOwner || isMonthWithinBudgetEditableWindow(monthInQuarter, budgetEditableStartMonth, budgetEditableEndMonth);
    return {
      editable: (editParams: EditableCallbackParams<any, any>) =>
        headerFor === HEADER_TYPES.ACTUAL
          ? false
          : isEditable &&
            monthEditableUnderBudgetWindow &&
            isUserAuthorizedToEditThisRow(editParams.data, userAlias, isFinanceAdminOrFinanceOwner, isBudgetLeader),
      field: monthInQuarter,
      headerName: convertMonthFormatToDisplay(monthInQuarter),
      wrapHeaderText: true,
      width: ColumnWidths.MONTH_COLUMN,
      suppressMovable: true,
      lockPinned: headerFor === HEADER_TYPES.ACTUAL,
      cellDataType: CellDataType.NUMBER,
      headerClass: ['ag-right-aligned-header'],
      cellStyle:
        headerFor === HEADER_TYPES.ACTUAL
          ? { textAlign: 'right', backgroundColor: FORECAST_GRID_MONTH_BACKGROUND_COLORS.ACTUAL_COLUMN }
          : { textAlign: 'right' },
      cellClassRules: {
        'bold-text': (params: CellClassParams) => params.node.footer,
        'actual-month-cell': () => headerFor === HEADER_TYPES.ACTUAL,
        'forecast-month-cell': () => headerFor === HEADER_TYPES.FORECAST,
        'editable-cell': (params: CellClassParams) =>
          isEditable &&
          monthEditableUnderBudgetWindow &&
          isUserAuthorizedToEditThisRow(params.data, userAlias, isFinanceAdminOrFinanceOwner, isBudgetLeader)
      },
      cellRenderer: 'agAnimateShowChangeCellRenderer',
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        showStepperButtons: false,
        preventStepping: true
      },
      valueParser: (params: ValueParserParams) => cellValueParser(params.newValue),
      valueFormatter: (params: ValueFormatterParams) => forecastCurrencyFormatter(params.value),
      aggFunc: 'sum',
      columnGroupShow: 'open'
    } as ColDef<any, any>;
  });
};

/**
 * Generates columns for each quarter including monthly and quarterly total columns
 * @param quarterWithMonthlyHeaderInfo - Array to hold the column definitions for the quarter
 * @param quarter - The quarter identifier (e.g., 'Q1', 'Q2')
 * @param monthColumnDefinitions - Array of column definitions for the months within the quarter
 * @param year - The year for the column definitions
 * @param headerFor - The header type (e.g., HEADER_TYPES.ACTUAL, 'Forecast')
 */
export const generateMonthsWithQuarterColumnDefinition = (
  quarterWithMonthlyHeaderInfo: ColDef<any, any>[],
  quarter: string,
  monthColumnDefinitions: ColDef<any, any>[],
  year: string,
  headerFor: string
): void => {
  // Add the monthly columns first
  quarterWithMonthlyHeaderInfo.push(...monthColumnDefinitions);

  // Define the quarterly total column
  const quarterlyTotalColumn: ColDef<any, any> = {
    editable: false, // Total columns are not editable
    field: `${year}-${quarter}-${headerFor}-Total`,
    headerName: `${quarter}-${year}`,
    headerClass: ['ag-right-aligned-header'],
    wrapHeaderText: true,
    width: ColumnWidths.QUARTER_COLUMN,
    suppressMovable: true,
    lockPinned: headerFor === HEADER_TYPES.ACTUAL,
    cellClass: headerFor === HEADER_TYPES.ACTUAL ? ['actual-quarter-cell'] : ['forecast-quarter-cell'],
    cellStyle: { textAlign: 'right', backgroundColor: FORECAST_GRID_MONTH_BACKGROUND_COLORS.QUARTERLY_YEARLY_TOTAL },
    cellClassRules: {
      'bold-text': (params: CellClassParams) => params.node.footer || false
    },
    cellRenderer: 'agAnimateShowChangeCellRenderer',
    valueParser: (params: ValueParserParams) => cellValueParser(params.newValue),
    valueFormatter: (params: ValueFormatterParams) => forecastCurrencyFormatter(params.value),
    valueGetter: (params: ValueGetterParams) => sumUpColumns(params, monthColumnDefinitions),
    aggFunc: 'sum',
    columnGroupShow: 'open'
  };

  // Add the quarterly total column for the 'open' state
  quarterWithMonthlyHeaderInfo.push({
    ...quarterlyTotalColumn,
    columnGroupShow: 'open'
  });

  // Add the quarterly total column for the 'closed' state (if required in future)
  // quarterWithMonthlyHeaderInfo.push({
  //   ...quarterlyTotalColumn,
  //   columnGroupShow: 'closed'
  // });
};

/**
 * Generates column definitions for a given year including quarterly and yearly total columns
 * @param year - The year for which the column definitions are generated
 * @param quarterWithMonthlyHeaderInfo - Array of quarter with monthly header information
 * @param headerFor - The header type (e.g., 'Actual', 'Forecast')
 * @returns ColDef - The column definition for the given year
 */
export const generateYearColumnDefinitions = (
  year: string,
  quarterWithMonthlyHeaderInfo: ColDef<any, any>[],
  headerFor: string
): ColDef<any, any> => {
  // Define the yearly total column
  const yearlyTotalColumn: ColDef = {
    editable: false, // Total columns are not editable
    headerName: `${year} Total`,
    headerClass: ['ag-right-aligned-header'],
    wrapHeaderText: true,
    width: ColumnWidths.YEAR_COLUMN,
    field: `${year}-${headerFor}-YearlyTotal`,
    suppressMovable: true,
    lockPinned: headerFor === HEADER_TYPES.ACTUAL,
    marryChildren: true,
    cellClass: headerFor === HEADER_TYPES.ACTUAL ? ['actual-year-cell'] : ['forecast-year-cell'],
    cellRenderer: 'agAnimateShowChangeCellRenderer',
    cellStyle:
      headerFor === HEADER_TYPES.ACTUAL
        ? { textAlign: 'right', backgroundColor: FORECAST_GRID_MONTH_BACKGROUND_COLORS.QUARTERLY_YEARLY_TOTAL }
        : { textAlign: 'right' },
    cellClassRules: {
      'bold-text': (params: CellClassParams) => params.node.footer
    },
    valueParser: (params: ValueParserParams) => cellValueParser(params.newValue),
    valueFormatter: (params: ValueFormatterParams) => forecastCurrencyFormatter(params.value),
    valueGetter: (params: ValueGetterParams) => sumUpColumns(params, quarterWithMonthlyHeaderInfo),
    aggFunc: 'sum'
  } as ColDef;

  // Create the year column definition including the quarterly and yearly total columns
  return {
    headerName: `${year}`,
    marryChildren: true,
    children: [
      ...quarterWithMonthlyHeaderInfo,
      {
        ...yearlyTotalColumn,
        columnGroupShow: 'open'
      },
      {
        ...yearlyTotalColumn,
        columnGroupShow: 'closed'
      }
    ]
  } as ColDef<any, any>;
};
